import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  ESSENTIALS_ARTICLE_EVENTS_PROPERTIES,
  ESSENTIALS_CONTENT_EVENTS_PROPERTIES,
  ESSENTIALS_CONTENT_ITEM_PROPERTIES,
  ESSENTIALS_QUERY_ID,
  EssentialsArticleRaw,
  EssentialsContentFormatted,
  formatEssentialsArticle,
  getEssentialsCmsAxiosInstance,
  OnEssentialsCardPressParams,
  translateToEssentialSiteCode,
} from '@lyrahealth-inc/shared-app-logic'

const emptyMixpanelProps = {}

/**
 * Fetches essentials articles data from goodness cms by article ids. Returns formatted article data and an essentials content on press handler.
 *
 * @param essentialsCmsUrl - string - Url to fetch data
 * @param articleIds - number[] -  article ids
 * @param graphQuery - string - query for graphql
 * @param userDisplayLanguage - string - users display language
 * @param trackEvent - function - function for tracking Mixpanel events
 * @param navigate - function - function for navigating to respective essentials content
 * @param defaultMixpanelProps - object - default Mixpanel properties to include w/track events
 */
export const useEssentialsFetcher = ({
  essentialsCmsUrl,
  articleIds,
  graphQuery,
  userDisplayLanguage,
  trackEvent,
  navigate,
  defaultMixpanelProps = emptyMixpanelProps,
  skipFetch = false,
  articleMetadata,
}: {
  essentialsCmsUrl: string
  articleIds: number[]
  graphQuery: string
  userDisplayLanguage: string
  trackEvent: (additionalMixpanelProps: { [key: string]: any }) => void
  navigate: ({ subPath, title, locale }: { subPath: string; title: string; locale: string }) => void
  defaultMixpanelProps?: { [key: string]: any }
  skipFetch?: boolean
  articleMetadata?: Record<number, { sectionTitle: string }>
}) => {
  const [essentialsArticleData, setEssentialsArticleData] = useState<EssentialsContentFormatted[]>([])
  const [essentialsArticleRawData, setEssentialsArticleRawData] = useState<EssentialsArticleRaw[]>([])
  const hasFetchedArticles = useRef(false)
  const isFetchingArticles = useRef(false)

  const onEssentialsCardPress = useCallback(
    ({
      subPath,
      title,
      contentFormat,
      contentType,
      destinationSlug,
      locale,
      contentTopics,
      contentSubtopics,
      contentTools,
      contentSubtools,
      contentId,
      index,
      isLyraSelect,
      isEnglish,
    }: OnEssentialsCardPressParams) => {
      const additionalMixpanelProps = {
        ...defaultMixpanelProps,
        [ESSENTIALS_CONTENT_EVENTS_PROPERTIES.ARTICLE_TITLE]: title,
        [ESSENTIALS_CONTENT_EVENTS_PROPERTIES.CONTENT_FORMAT]: contentFormat,
        [ESSENTIALS_CONTENT_EVENTS_PROPERTIES.CONTENT_TYPE]: contentType,
        [ESSENTIALS_CONTENT_EVENTS_PROPERTIES.DESTINATION_SLUG]: destinationSlug,
        [ESSENTIALS_ARTICLE_EVENTS_PROPERTIES.CONTENT_TOPICS]:
          articleMetadata?.[Number(contentId)]?.sectionTitle ?? contentTopics,
        [ESSENTIALS_ARTICLE_EVENTS_PROPERTIES.CONTENT_SUBTOPICS]: contentSubtopics,
        [ESSENTIALS_ARTICLE_EVENTS_PROPERTIES.CONTENT_TOOLS]: contentTools,
        [ESSENTIALS_ARTICLE_EVENTS_PROPERTIES.CONTENT_SUBTOOLS]: contentSubtools,
        [ESSENTIALS_ARTICLE_EVENTS_PROPERTIES.CONTENT_ID]: contentId,
      }
      const lyraSelectAdditionalMixpanelProps = {
        ...defaultMixpanelProps,
        [ESSENTIALS_CONTENT_ITEM_PROPERTIES.ITEM_CLICKED]: {
          [ESSENTIALS_CONTENT_ITEM_PROPERTIES.ITEM_ID]: contentId,
          [ESSENTIALS_CONTENT_ITEM_PROPERTIES.ITEM_TYPE]: contentFormat,
          [ESSENTIALS_CONTENT_ITEM_PROPERTIES.ITEM_RANK]: index ? index + 1 : 0,
        },
      }
      trackEvent(isLyraSelect ? lyraSelectAdditionalMixpanelProps : additionalMixpanelProps)
      navigate({ subPath, title, locale: isEnglish ? 'en' : locale })
    },
    [articleMetadata, defaultMixpanelProps, navigate, trackEvent],
  )

  useEffect(() => {
    const fetchCMS = async (locale: string) => {
      isFetchingArticles.current = true
      const GRAPHQL_VARIABLES = {
        id: articleIds,
        site: translateToEssentialSiteCode(locale),
      }

      const data = {
        query: graphQuery,
        variables: GRAPHQL_VARIABLES,
      }

      try {
        if (!essentialsCmsUrl) {
          throw new Error('CMS URL is empty')
        }

        const instance = getEssentialsCmsAxiosInstance(essentialsCmsUrl)
        const response = await instance.post(`?${ESSENTIALS_QUERY_ID.GET_ARTICLES}=true`, data)

        if (response.status != 200) {
          throw new Error('Invalid response received while fetching essentials articles')
        } else {
          const responseData = response.data?.data?.entries
          const essentialsContent: { raw: EssentialsArticleRaw; formatted: EssentialsContentFormatted }[] =
            responseData.map((data: EssentialsArticleRaw) => {
              return { raw: data, formatted: formatEssentialsArticle(data, onEssentialsCardPress, locale) }
            })
          return essentialsContent
        }
      } catch (e) {
        console.error('Failed to fetch essentials articles ' + e)
        return []
      } finally {
        isFetchingArticles.current = false
      }
    }

    if (skipFetch || hasFetchedArticles.current || isFetchingArticles.current) {
      return
    }

    Promise.all([fetchCMS('en-US'), userDisplayLanguage !== 'en-US' ? fetchCMS(userDisplayLanguage) : null])
      .then(([englishContents, localizedContents]) => {
        hasFetchedArticles.current = true
        return articleIds
          .map((id) => {
            const localizedItem = localizedContents?.find((item) => item.formatted.id == id)
            if (localizedItem) {
              localizedItem.formatted.isEnglish = false
              localizedItem.raw.isEnglish = false
            }
            const englishFallback = englishContents.find((item) => item.formatted.id == id)
            if (englishFallback) {
              englishFallback.formatted.isEnglish = true
              englishFallback.raw.isEnglish = true
            }
            return localizedItem ?? englishFallback
          })
          .filter(Boolean) as { raw: EssentialsArticleRaw; formatted: EssentialsContentFormatted }[]
      })
      .then((result) => {
        setEssentialsArticleData(result.map((item) => item.formatted))
        setEssentialsArticleRawData(result.map((item) => item.raw))
      })
  }, [articleIds, essentialsCmsUrl, graphQuery, onEssentialsCardPress, skipFetch, userDisplayLanguage])

  return useMemo(() => {
    return { essentialsArticleData, essentialsArticleRawData, onEssentialsCardPress }
  }, [essentialsArticleData, essentialsArticleRawData, onEssentialsCardPress])
}
